import React, { useState } from "react";

import { makeStyles } from "tss-react/mui";
import {
  Button,
  Dialog,
  DialogActions,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { ptBR } from "date-fns/locale";
import { DateCalendar, DateView } from "@mui/x-date-pickers";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import { findMonth, findWeek, findYear } from "logic/DateSelectors";

const BR_TIMEZONE = 3;

const isSameWeek = (dayDisplayed, dayCompared): boolean => {
  const [sunday, saturday] = findWeek(dayCompared);

  // MUI gives dates in UTC
  const timedDayDisplayed = new Date(dayDisplayed);
  timedDayDisplayed.setHours(dayDisplayed.getHours() + BR_TIMEZONE);

  return sunday <= timedDayDisplayed && saturday >= timedDayDisplayed;
};

const PickerDayWeek = ({ selectedDay, day, ...others }) => {
  return (
    // @ts-ignore MUI's weird typings in action
    <PickersDay {...others} day={day} selected={isSameWeek(day, selectedDay)} />
  );
};

const useStyles = makeStyles()((theme) => ({
  outer: {
    padding: theme.spacing(0),
    marginBottom: theme.spacing(2),
  },
  dateStyles: {
    textTransform: "lowercase",
    "&::first-letter": {
      textTransform: "capitalize",
    },
  },
}));

const GRANULARITIES_DISPLAY: Record<
  SelectorGranularitiesProps["granularity"],
  Intl.DateTimeFormatOptions
> = {
  day: {},
  week: {},
  month: {
    month: "long",
    year: "numeric",
  },
  year: {
    year: "numeric",
  },
};

const GRANULARITIES_VIEWS: Partial<
  Record<SelectorGranularitiesProps["granularity"], Array<DateView>>
> = {
  day: ["day", "month", "year"],
  week: ["day", "month", "year"],
  month: ["month", "year"],
};

export const SelectorGranularities = ({
  startDate,
  endDate,
  onDateChange,
  granularity,
}: SelectorGranularitiesProps) => {
  const { classes } = useStyles();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handleButtonPress = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setSelectedDate(null);
    setIsModalOpen(false);
  };

  const handleSelection = () => {
    if (selectedDate) {
      let startDate, endDate;
      switch (granularity) {
        case "week":
          [startDate, endDate] = findWeek(selectedDate);
          break;
        case "month":
          [startDate, endDate] = findMonth(selectedDate);
          break;
        case "year":
          [startDate, endDate] = findYear(selectedDate);
          break;
        default:
          // daily falls here
          startDate = selectedDate;
      }
      onDateChange(startDate, endDate);
    }
    handleModalClose();
  };

  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString(
      ptBR.code,
      GRANULARITIES_DISPLAY[granularity]
    );
  };

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      className={classes.outer}
    >
      <Typography
        variant="body1"
        color="text.primary"
        className={classes.dateStyles}
      >
        {startDate ? formatDisplayDate(startDate) : <Skeleton />}
        {endDate &&
          granularity === "week" &&
          ` a ${formatDisplayDate(endDate)}`}
      </Typography>

      <Button color="secondary" variant="outlined" onClick={handleButtonPress}>
        <Typography variant="caption">Alterar</Typography>
      </Button>

      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <Grid container>
          <DateCalendar
            disableHighlightToday
            disableFuture
            showDaysOutsideCurrentMonth
            value={selectedDate || startDate}
            onChange={setSelectedDate}
            views={GRANULARITIES_VIEWS[granularity]}
            // @ts-ignore
            slots={granularity === "week" ? { day: PickerDayWeek } : undefined}
            slotProps={
              granularity === "week"
                ? {
                    day: (ownerState) =>
                      ({
                        selectedDay: selectedDate || startDate,
                      } as any),
                  }
                : undefined
            }
          />
        </Grid>
        <DialogActions>
          <Button variant="outlined" onClick={handleModalClose}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSelection}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export type SelectorGranularitiesProps = {
  /* First date to be displayed */
  startDate: Date;
  /* Second date displayed in the non-daily views */
  endDate?: Date;
  /* Handler for when the user saves the changes, receives the end date of selected time period */
  onDateChange: (start: Date, end?: Date) => void;
  /* date chunks the user will be allowed to pick */
  granularity: "day" | "week" | "month" | "year";
};
